import type {
    FCSEvent,
    MemberRoom,
    MemberRoomDraft,
    MemberRoomPacket,
    MembersRecent,
    MemberWebcam,
} from '../../types'
import type { RouteHandlerOptions } from '../../types/state'
import { HEALTH, MembersAction } from '../constants/enums'
import { locales } from '../locales/en'
import {
    fetchMembersByTag,
    fetchMemberTags,
    MEMBER_TAGS_SCRIPT,
    parseMemberTagsPage,
} from '../mfcLogic/api/memberTags'
import { wwwPhpRequest } from '../mfcLogic/api/wwwPhp'
import type { RoomJoinBlock } from '../mfcLogic/memberAccess'
import {
    findMemberRoomByOwner,
    isOwnerWebcamRoom,
    memberRoomCreateError,
    memberRoomCreatePayload,
    sanitizeMemberRoomText,
    toDraftMemberRoom,
    toMemberRoom,
} from '../mfcLogic/memberRooms'
import {
    getMembersMockMode,
    isMembersMock,
    MembersMockMode,
} from '../mfcLogic/membersMock'
import {
    MOCK_MY_TAGS,
    MOCK_MY_WEBCAM,
    MOCK_RECENT,
    MOCK_ROOMS_BY_ID,
    MOCK_TAGGED,
    MOCK_TAGS,
    MOCK_WEBCAMS,
    mockRoomsWithoutOwners,
} from '../mfcLogic/membersMockData'
import {
    addRecentMemberRoom,
    addRecentMemberWebcam,
    readMembersRecent,
    removeRecentMemberRoom,
    writeMembersRecent,
} from '../mfcLogic/membersRecent'
import {
    membersPath,
    MembersQuery,
    membersQueryFrom,
    typedMembersQuery,
} from '../mfcLogic/membersRoute'
import {
    CHANNEL_ID_START,
    FCCHAN_JOIN,
    FCCHAN_PART,
    FCCHAN_WELCOME,
    FCRESPONSE_ERROR,
} from '../mfcLogic/mfcConstants'
import {
    defaultMembersState,
    HEALTHSTATE,
    TransitionTypes,
    USERSSTATE,
} from '../state'
import { getMembersState, getRoomsState } from '../state/getState'
import { slugToID } from '../state/internal/users'
import { openWatchMemberWebcamConfirmation } from '../state/modals/state.confirmation'
import {
    closeDrawer,
    openMemberTagsDrawer,
    openTokensDrawer,
} from '../state/modals/state.drawer'
import {
    clearMemberWebcams,
    markMemberRoomJoin,
    markMemberRoomsStale,
    removeMemberRoom,
    resetMemberRooms,
    resetMembersState,
    setMemberCreateRoomError,
    setMemberRoom,
    setMemberRooms,
    setMemberRoomsError,
    setMembersComposing,
    setMembersLoading,
    setMembersOwnUserId,
    setMembersRecent,
    setMemberTaggedError,
    setMemberTaggedLoading,
    setMemberTaggedMembers,
    setMemberTagList,
    setMemberTagSelected,
    setMemberTagsError,
    setMemberTagsLoading,
    setMemberWebcams,
    setMemberWebcamsError,
    setMyMemberTags,
    setMyMemberTagsError,
    setMyMemberTagsLoading,
    setMyMemberWebcam,
    takeMemberRoomJoin,
} from '../state/state.members'
import {
    closeRoom,
    openRoom,
    setMemberRoomClosedState,
    setRoomMessagesState,
} from '../state/state.rooms'
import { AppRouter } from '../state/state.router'
import { routerPush } from '../utilities/router'
import { fill } from '../utilities/string'
import { openMenuDialog } from './menu'
import { goToMessage } from './nav'
import { membersAction } from './workerActions'

const tagRequests = {
    list: null as AbortController | null,
    members: null as AbortController | null,
}

const nextTagController = (key: keyof typeof tagRequests) => {
    tagRequests[key]?.abort()
    tagRequests[key] = new AbortController()
    return tagRequests[key]
}

const settleTagRequest = (
    key: keyof typeof tagRequests,
    request: AbortController,
) => {
    if (tagRequests[key] === request) tagRequests[key] = null
}

const abortTagRequests = () => {
    tagRequests.list?.abort()
    tagRequests.members?.abort()
    tagRequests.list = null
    tagRequests.members = null
}

const ownUserId = () => USERSSTATE.users[slugToID['me']]?.id ?? null

const seedMock = () => {
    const mode = getMembersMockMode()
    resetMembersState(defaultMembersState())
    setMembersOwnUserId(ownUserId())
    if (mode === MembersMockMode.LOADING) {
        setMembersLoading(true)
        setMemberTagsLoading(true)
        setMemberTaggedLoading()
        return
    }
    if (mode === MembersMockMode.ERROR) {
        setMemberWebcamsError({ errmsg: locales.members.errors.webcams })
        setMemberRoomsError({ errmsg: locales.members.errors.rooms })
        setMemberTagsError({ errmsg: locales.members.errors.tags })
        return
    }
    if (mode === MembersMockMode.EMPTY) {
        setMemberWebcams([])
        setMemberRooms({})
        setMemberTagList([])
        return
    }
    setMemberWebcams(MOCK_WEBCAMS)
    setMyMemberWebcam({
        ...MOCK_MY_WEBCAM,
        id: ownUserId() ?? MOCK_MY_WEBCAM.id,
    })
    setMemberRooms(
        mode === MembersMockMode.BASIC
            ? mockRoomsWithoutOwners([ownUserId(), MOCK_MY_WEBCAM.id])
            : MOCK_ROOMS_BY_ID,
    )
    setMemberTagList(MOCK_TAGS)
    setMyMemberTags(MOCK_MY_TAGS)
    setMembersRecent(MOCK_RECENT)
}

const CREATE_ROOM_TIMEOUT = 10000

// the UCR room list stays joined while the page, a create, or a member room the viewer is in needs it
const roomList = {
    session: false,
    page: false,
    joined: false,
    loaded: false,
    inRooms: new Set<number>(),
    collected: {} as Record<number, MemberRoom>,
    beforeReset: new Set<number>(),
}

const roomCreate = {
    join: false,
    previousRoomId: null as number | null,
    timer: null as ReturnType<typeof setTimeout> | null,
}

const wantsRoomList = () =>
    roomList.page || !!roomCreate.timer || roomList.inRooms.size > 0

const setRoomListJoined = (joined: boolean) => {
    roomList.joined = joined
    roomList.loaded = false
    roomList.collected = {}
}

const syncMemberRoomList = () => {
    if (!roomList.session) return
    const wanted = wantsRoomList()
    if (wanted && !roomList.joined) {
        setRoomListJoined(true)
        membersAction(MembersAction.ROOM_LIST, { join: true })
        return
    }
    if (wanted || !roomList.joined || !roomList.loaded) return
    setRoomListJoined(false)
    markMemberRoomsStale()
    membersAction(MembersAction.ROOM_LIST, { join: false })
}

const closeMemberRoom = (id: number) => {
    removeMemberRoom(id)
    setMemberRoomClosedState(id - CHANNEL_ID_START)
}

const commitMemberRooms = (rooms: Record<number, MemberRoom>) => {
    const known = new Set([
        ...roomList.beforeReset,
        ...Object.keys(getMembersState().rooms).map(Number),
    ])
    roomList.beforeReset.clear()
    known.forEach((id) => {
        if (!rooms[id]) setMemberRoomClosedState(id - CHANNEL_ID_START)
    })
    setMemberRooms(rooms)
}

const endMemberRoomCreate = () => {
    clearTimeout(roomCreate.timer)
    roomCreate.timer = null
}

const failMemberRoomCreate = (errmsg?: string) => {
    if (!roomCreate.timer) return
    endMemberRoomCreate()
    setMemberCreateRoomError({ errmsg })
    syncMemberRoomList()
}

// the viewer's new room is listed: the create succeeded, and may join it
const settleMemberRoomCreate = () => {
    const { myRoomId, rooms } = getMembersState()
    if (!roomCreate.timer || !myRoomId) return
    if (myRoomId === roomCreate.previousRoomId) return
    const room = rooms[myRoomId]
    if (!room) return
    endMemberRoomCreate()
    if (roomCreate.join) openJoinedMemberRoom(room)
    else syncMemberRoomList()
}

export const startMemberRoomsSession = () => {
    roomList.session = true
    setRoomListJoined(false)
    markMemberRoomsStale()
    syncMemberRoomList()
}

export const endMemberRoomsSession = () => {
    if (!roomList.session) return
    roomList.session = false
    setRoomListJoined(false)
    Object.keys(getMembersState().rooms).forEach((id) =>
        roomList.beforeReset.add(Number(id)),
    )
    resetMemberRooms()
    failMemberRoomCreate()
}

export const onMemberRoomPacket = (data: FCSEvent['data']) => {
    if (!data || !roomList.joined) return
    const { arg1: channelId, arg2: action, payload } = data
    if (!channelId) {
        if (roomList.loaded) return
        if (action === FCRESPONSE_ERROR) {
            setRoomListJoined(false)
            setMemberRoomsError({})
            failMemberRoomCreate()
        } else if (action === FCCHAN_WELCOME) {
            roomList.loaded = true
            commitMemberRooms(roomList.collected)
            roomList.collected = {}
            settleMemberRoomCreate()
            syncMemberRoomList()
        }
        return
    }
    if (action === FCCHAN_PART) {
        if (roomList.loaded) closeMemberRoom(channelId)
        else delete roomList.collected[channelId]
        return
    }
    if (action !== FCCHAN_JOIN || !payload) return
    const room = toMemberRoom(channelId, payload as MemberRoomPacket)
    if (!roomList.loaded) {
        roomList.collected[channelId] = room
        return
    }
    setMemberRoom(room)
    settleMemberRoomCreate()
}

export const onMemberRoomCreateReply = (data: FCSEvent['data']) => {
    const error = data && memberRoomCreateError(data)
    if (error) failMemberRoomCreate(error.errmsg)
}

export const enterMemberRoomList = (id: number) => {
    roomList.inRooms.add(id)
    syncMemberRoomList()
}

export const leaveMemberRoomList = (id: number) => {
    if (roomList.inRooms.delete(id)) syncMemberRoomList()
}

export const subscribeMembers = () => {
    if (isMembersMock()) {
        seedMock()
        return
    }
    loadMembersRecent()
    setMembersLoading(true)
    roomList.page = true
    syncMemberRoomList()
    membersAction(MembersAction.SUBSCRIBE)
}

export const unsubscribeMembers = () => {
    abortTagRequests()
    if (isMembersMock()) return
    clearMemberWebcams()
    roomList.page = false
    syncMemberRoomList()
    membersAction(MembersAction.UNSUBSCRIBE)
}

export const refreshMembers = () => {
    if (isMembersMock()) {
        seedMock()
        return
    }
    setMembersLoading(true)
    membersAction(MembersAction.REFRESH)
}

export const refreshMemberRooms = () => {
    if (isMembersMock()) {
        seedMock()
        return
    }
    setMemberRoomsError(null)
    syncMemberRoomList()
}

export const currentMembersQuery = () =>
    AppRouter.route.basePage === 'members'
        ? membersQueryFrom(
              AppRouter.route.baseQuery as Record<string, string> | undefined,
          )
        : {}

const membersRoute = (patch: MembersQuery, options: RouteHandlerOptions = {}) =>
    routerPush(membersPath({ ...currentMembersQuery(), ...patch }), options)

// the search text lives in the URL; typing replaces the entry instead of stacking history
export const setMembersQuery = (query: string) => {
    const next = typedMembersQuery(currentMembersQuery(), query)
    if (next) routerPush(membersPath(next), { replaceState: true })
}

export const clearMembersSearch = () =>
    membersRoute({ query: undefined, tag: undefined }, { replaceState: true })

export const openMemberTag = (tag: string) =>
    membersRoute({ query: undefined, tag })

// a member link and a room link never travel together: each would act again on remount
export const selectMemberWebcam = (name: string | null) =>
    membersRoute({ member: name ?? undefined, room: undefined })

export const openMemberWebcamEntry = (name: string) =>
    membersRoute({ member: name, room: undefined })

// a room link joins once, even when the page remounts under it
const roomLink = { owner: null as number | null }

export const releaseMemberRoomLink = () => {
    roomLink.owner = null
}

export const claimMemberRoomLink = (owner: number) => {
    if (roomLink.owner === owner) return false
    roomLink.owner = owner
    return true
}

// a new link to a member's room: /members joins it once the list is fresh, or says it closed
export const openMemberRoomLink = (
    ownerName: string,
    options: RouteHandlerOptions = {},
) => {
    if (currentMembersQuery().room?.toLowerCase() !== ownerName.toLowerCase()) {
        releaseMemberRoomLink()
    }
    return membersRoute({ room: ownerName, member: undefined }, options)
}

export const createMemberRoom = (draft: MemberRoomDraft) => {
    setMemberCreateRoomError(null)
    if (!isMembersMock()) {
        if (
            !roomList.session ||
            HEALTHSTATE.health.status !== HEALTH.CONNECTED
        ) {
            setMemberCreateRoomError({})
            return
        }
        endMemberRoomCreate()
        roomCreate.join = draft.join
        roomCreate.previousRoomId = getMembersState().myRoomId
        roomCreate.timer = setTimeout(
            () => failMemberRoomCreate(),
            CREATE_ROOM_TIMEOUT,
        )
        syncMemberRoomList()
        membersAction(MembersAction.CREATE_ROOM, memberRoomCreatePayload(draft))
        return
    }
    if (getMembersMockMode() === MembersMockMode.BASIC) return
    const ownerId = ownUserId() ?? MOCK_MY_WEBCAM.id
    setMembersOwnUserId(ownerId)
    setMemberRoom(
        toDraftMemberRoom(
            draft,
            ownerId,
            USERSSTATE.users[ownerId]?.name ?? MOCK_MY_WEBCAM.name,
        ),
    )
}

export const closeOwnMemberRoom = () => {
    if (!isMembersMock()) {
        if (roomList.session) membersAction(MembersAction.CLOSE_ROOM)
        return
    }
    const { myRoomId } = getMembersState()
    if (myRoomId) removeMemberRoom(myRoomId)
}

const rememberRecent = (update: (recent: MembersRecent) => MembersRecent) => {
    const recent = update(getMembersState().recent)
    setMembersRecent(recent)
    if (!isMembersMock()) writeMembersRecent(recent)
}

export const loadMembersRecent = () => {
    if (isMembersMock()) return
    setMembersRecent(readMembersRecent())
}

export const forgetRecentMemberRoom = (ownerId: number) => {
    rememberRecent((recent) => removeRecentMemberRoom(recent, ownerId))
    if (getRoomsState()[ownerId]?.isMemberRoom) {
        closeRoom(ownerId, { destroy: true })
    }
}

export const clearRecentMemberRooms = () => {
    getMembersState().recent.rooms.forEach(({ ownerId }) => {
        if (getRoomsState()[ownerId]?.isMemberRoom) {
            closeRoom(ownerId, { destroy: true })
        }
    })
    rememberRecent((recent) => ({ ...recent, rooms: [] }))
}

export const joinedMemberRoom = (ownerId: number) => {
    const requested = takeMemberRoomJoin(ownerId)
    if (!requested && !getRoomsState()[ownerId]?.isMemberRoom) return undefined
    return findMemberRoomByOwner(getMembersState().rooms, ownerId)
}

const openJoinedMemberRoom = (
    room: MemberRoom,
    options: Pick<RouteHandlerOptions, 'transitionSource'> = {},
) => {
    markMemberRoomJoin(room.ownerId)
    openRoom(room.ownerName || room.ownerId, { ...options, memberRoom: true })
}

export const joinMemberRoom = (roomId: number) => {
    const room = getMembersState().rooms[roomId]
    if (!room) return
    rememberRecent((recent) => addRecentMemberRoom(recent, room))
    openJoinedMemberRoom(room, { transitionSource: TransitionTypes.ROW })
}

export const joinMemberRoomOrUpsell = (
    roomId: number,
    block: RoomJoinBlock,
) => {
    if (block === null) joinMemberRoom(roomId)
    else if (block === 'premium') openTokensDrawer()
}

export const refetchFailedMemberTag = () => {
    const { selected, membersError, membersLoading } = getMembersState().tags
    if (!selected || isMembersMock()) return
    if (membersError || (membersLoading && !tagRequests.members)) {
        selectMemberTag(selected)
    }
}

export const loadMemberTags = () => {
    if (isMembersMock()) return
    const request = nextTagController('list')
    setMemberTagsLoading(true)
    fetchMemberTags(request.signal)
        .then((list) => {
            settleTagRequest('list', request)
            setMemberTagList(list)
            refetchFailedMemberTag()
        })
        .catch((error) => {
            if (error?.name === 'AbortError') return
            settleTagRequest('list', request)
            setMemberTagsError({
                err: error?.err,
                errmsg: locales.members.errors.tags,
            })
        })
}

export const selectMemberTag = (tag: string) => {
    setMemberTagSelected(tag)
    if (isMembersMock()) {
        setMemberTaggedMembers(
            MOCK_TAGGED.filter((member) => member.tags.includes(tag)),
        )
        return
    }
    const request = nextTagController('members')
    setMemberTaggedLoading()
    fetchMembersByTag(tag, request.signal)
        .then((members) => {
            settleTagRequest('members', request)
            setMemberTaggedMembers(members)
        })
        .catch((error) => {
            if (error?.name === 'AbortError') return
            settleTagRequest('members', request)
            setMemberTaggedError({
                err: error?.err,
                errmsg: locales.members.errors.tagged,
            })
        })
}

export const clearMemberTag = () => setMemberTagSelected('')

export const retryMemberTag = () => {
    const { selected } = getMembersState().tags
    if (selected) selectMemberTag(selected)
}

export const loadMyMemberTags = () => {
    if (isMembersMock()) {
        setMyMemberTags(getMembersState().tags.mine)
        return
    }
    setMyMemberTagsLoading()
    wwwPhpRequest({ script: MEMBER_TAGS_SCRIPT, method: 'GET' })
        .then((html) => setMyMemberTags(parseMemberTagsPage(html)))
        .catch((error) => {
            setMyMemberTagsError({
                err: error?.err,
                errmsg: locales.members.errors.yourTags,
            })
        })
}

export const applySavedMemberTags = (tags: Array<string>) => {
    setMyMemberTags(tags)
    closeDrawer(true)
}

export const startCreateMemberRoom = () => {
    setMemberCreateRoomError(null)
    setMembersComposing(true)
}

export const cancelCreateMemberRoom = () => setMembersComposing(false)

export const openMemberTagsEditor = () => {
    openMemberTagsDrawer()
    loadMyMemberTags()
}

export const announceMemberRoom = async (room: MemberRoom) => {
    const ownerCam = isOwnerWebcamRoom(room)
    const welcome = fill(
        ownerCam
            ? locales.members.room.welcome
            : locales.members.room.welcomeLounge,
        {
            room: sanitizeMemberRoomText(room.name),
            owner: room.ownerName,
        },
    )
    const description = sanitizeMemberRoomText(room.description)
    const notice = (text: string) =>
        setRoomMessagesState(
            { system: true, info: true, timestamp: Date.now(), text },
            room.ownerId,
        )
    await notice(description ? `${welcome} ${description}` : welcome)
    if (!ownerCam) await notice(locales.members.room.loungeNote)
}

export const watchMemberWebcam = (
    member: Pick<MemberWebcam, 'id' | 'name' | 'color'>,
) =>
    new Promise<void>((resolve) => {
        openWatchMemberWebcamConfirmation(
            member.name,
            async () => {
                rememberRecent((recent) =>
                    addRecentMemberWebcam(recent, member),
                )
                await goToMessage(member.name)
                resolve()
            },
            resolve,
        )
    })

export const leaveMemberRoom = (ownerId: number) =>
    closeRoom(ownerId, { destroy: true })

export const openMemberMenu = (id: number) =>
    void openMenuDialog(id, { hideNextPrev: true })
